import * as React from 'react';
import { Venues } from '@weddingspot/ws-api-client';
import { FeatureFlagContext, FLAG_SHOPPING_CART_FOR_SRP, ReportDMCAModal, ShoppingCartContext, Slider, SubHeaderTypes } from '@weddingspot/ws-core-components';

import dynamic from 'next/dynamic';
import useInView from 'react-cool-inview';

import styles from './styles.module.scss';
import { Gallery } from './Gallery/Gallery';

import AmenitiesRequirements from './sections/amenities';
import VenueNotes from './sections/VenueNotes';

import { VenueProfileContext } from './context';
import { StickyContainer } from 'react-sticky';
import { useEffect } from 'react';
import { setCurUserProps } from '@weddingspot/ws-analytics';
import ShortInfo from './sections/ShortInfo/ShortInfo';

const VenueRecommendations = dynamic(() => import('./sections/recomendations/VenueRecommendations'), { ssr: false });
const LazyBody = dynamic(() => import('./components/LazyBody'), { ssr: false });
const NavbarLazy = dynamic(() => import('./sections/navbar'), { ssr: false });
const VenueHeader = dynamic(() => import('./sections/header'));
const SubHeader = dynamic(() => import('../header/SubHeader'));
const Cta = dynamic(() => import('./sections/CTA/CTA'));
const Deal = dynamic(() => import('./sections/Deal/Deal'));

const VenueProfile = () => {
    const context = React.useContext(VenueProfileContext);
    const { setCustomButtonStyle } = React.useContext(ShoppingCartContext);
    const { observe: recomendationsObserve, inView: recomendationsInView } = useInView({
        onEnter: ({ unobserve }) => unobserve(),
    });
    const { observe: lazyBodyObserve, inView: lazyBodyInView } = useInView({
        onEnter: ({ unobserve }) => unobserve(),
    });

    const { flagEnabled, isInitialized: isFlagsInitialized } = React.useContext(FeatureFlagContext);
    const shoppingCartFlag = isFlagsInitialized && flagEnabled(FLAG_SHOPPING_CART_FOR_SRP);

    const [reportModalShowing, setReportModalShowing] = React.useState(false);

    const closeReportModal= () => {
        setReportModalShowing(false);
    }

    const openReportModal= () => {
        setReportModalShowing(true);
    }

    React.useEffect(() => {
        if (shoppingCartFlag) {
            setCurUserProps({
                'Profile page with cart ': 'true',
            });
        }
    }, [shoppingCartFlag]);

    useEffect(() => {
        setCustomButtonStyle({
            zIndex: '21',
            bottom: '80px',
        });
    }, []);

    const wsVenueProfile = React.useMemo(() => context.wsVenueProfile, []) as Venues.VenueDetails;
    // TODO: Use Carina layout
    return (
        <div className={styles.container}>
            <div className={styles.SubHeader}>
                <SubHeader headerType={SubHeaderTypes.venueProfile} />
            </div>

            <div className={styles.section}>
                <VenueHeader {...wsVenueProfile} />
            </div>

            <StickyContainer>
                <div className={styles.ordered}>
                    <NavbarLazy />
                    <Gallery {...wsVenueProfile} />

                    <Slider
                        venueName={wsVenueProfile.name}
                        onReportClick={openReportModal}
                        venueProfileVersionThree={true}
                    />
                            
                    <ReportDMCAModal
                        venueId={wsVenueProfile.id}
                        showModal={reportModalShowing}
                        handleClose={closeReportModal}
                    />
                </div>

                <StickyContainer className={styles.containerForCTA}>
                    <Cta />
                    <div className={styles.sectionLeft}>
                        <Deal />
                        <ShortInfo />
                        <div className={styles.description}>
                            <h2>{wsVenueProfile.name } Overview</h2>
                            {wsVenueProfile.description}
                        </div>

                        {wsVenueProfile.rental_fee_description && (
                            <div id='venueNotes'>
                                <VenueNotes venueNotes={wsVenueProfile.rental_fee_description} />
                            </div>
                        )}

                        {wsVenueProfile.amenities && wsVenueProfile.special_restrictions && (
                            <div id='amenities'>
                                <AmenitiesRequirements
                                    amenities={wsVenueProfile.amenities.sort()}
                                    requirements={wsVenueProfile.special_restrictions.sort()}
                                />
                            </div>
                        )}

                        <div ref={lazyBodyObserve}>{lazyBodyInView && <LazyBody />}</div>
                    </div>
                </StickyContainer>

                {!wsVenueProfile.disable_recommendations && (
                    <div className={styles.bottom} ref={recomendationsObserve}>
                        <div className={styles.content}>{recomendationsInView && <VenueRecommendations />}</div>
                    </div>
                )}
            </StickyContainer>
        </div>
    );
};

export default VenueProfile;
